<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { Button, Input, RadioGroup, RadioGroupItem } from "@/shared/ui";

const props = defineProps<{
  candidates: { masterId: string; name: string }[];
  selectedMasterId: string | null;
  showSearch: boolean;
  pickerSearch: string;
}>();

const emit = defineEmits<{
  select: [masterId: string];
  next: [];
  createNew: [];
  "update:pickerSearch": [value: string];
}>();

const { t } = useI18n();

const radioValue = computed({
  get: () => props.selectedMasterId ?? "",
  set: (val: string) => emit("select", val),
});
</script>

<template>
  <div class="master-picker">
    <!-- Search input: visible only when showSearch -->
    <Input
      v-if="showSearch"
      :model-value="pickerSearch"
      autofocus
      :placeholder="t('services.assign.picker.search')"
      data-testid="picker-search"
      class="master-picker__search"
      @update:model-value="emit('update:pickerSearch', $event)"
    />

    <!-- Candidates list -->
    <RadioGroup
      v-model="radioValue"
      class="master-picker__list"
    >
      <label
        v-for="candidate in candidates"
        :key="candidate.masterId"
        class="master-picker__item"
        data-testid="picker-candidate"
      >
        <RadioGroupItem :value="candidate.masterId" :label="candidate.name" />
      </label>
    </RadioGroup>

    <!-- Footer -->
    <div class="master-picker__footer">
      <Button
        variant="default"
        :disabled="selectedMasterId === null"
        data-testid="picker-next"
        @click="emit('next')"
      >
        {{ t("services.assign.picker.next") }}
      </Button>
      <Button
        variant="ghost"
        data-testid="create-new-master"
        @click="emit('createNew')"
      >
        {{ t("services.assign.picker.createNew") }}
      </Button>
    </div>
  </div>
</template>

<style scoped>
.master-picker {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.master-picker__search {
  width: 100%;
}

.master-picker__list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.master-picker__item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: var(--radius);
}

.master-picker__item:hover {
  background-color: var(--accent);
}


.master-picker__footer {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
</style>
